import {useState, useEffect, useRef} from 'react';

type Props = {
  checkIsReady: () => boolean;
  intervalMs?: number;
  maxTries?: number;
};

/**
 * @description Custom hook to wait for an SDK to be ready.
 * It checks the readiness of the SDK at regular intervals until it is ready or the maximum number of attempts is reached.
 * If the SDK is not ready after the maximum attempts, it sets an error state.
 * Useful for script injected SDKs that load asynchronously and need to be ready before use.
 * @param param - Options for the hook
 * @param param.checkIsReady - Function to check if the SDK is ready
 * @param param.intervalMs - Interval in milliseconds to check the SDK readiness (default: 100)
 * @param param.maxTries - Maximum number of attempts to check the SDK readiness (default: 50)
 * @return {ready: boolean, error: string | null, attempts: number}
 * - `ready`: Indicates if the SDK is ready
 * - `error`: Error message if the SDK is not ready after maximum attempts
 * - `attempts`: Number of attempts made to check the SDK readiness
 */
const useWaitForSdkReady = ({checkIsReady, intervalMs = 100, maxTries = 50}: Props) => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    let tries = 0;

    intervalRef.current = setInterval(() => {
      if (checkIsReady()) {
        clearInterval(intervalRef.current);
        setIsReady(true);
        return;
      }

      tries += 1;
      setAttempts(tries);

      if (tries >= maxTries) {
        clearInterval(intervalRef.current);
        setError('SDK not ready after maximum attempts');
      }
    }, intervalMs);

    return () => clearInterval(intervalRef.current);
  }, [checkIsReady, intervalMs, maxTries]);

  return {isReady, error, attempts};
};

export default useWaitForSdkReady;
